import type { SVGAttributes } from 'react';

/**
 * The site's ONE icon set. Every icon is a hand-drawn 24x24 line glyph,
 * 1.75px stroke, round caps/joins — no mixing in a second icon library or a
 * filled/duotone variant. If a new icon is needed, add it here in the same
 * style rather than reaching for react-icons/lucide/etc.
 */
const paths = {
  menu: 'M3 6h18M3 12h18M3 18h18',
  close: 'M6 6l12 12M18 6L6 18',
  chevronDown: 'M6 9l6 6 6-6',
  chevronRight: 'M9 6l6 6-6 6',
  star: 'M12 3.5l2.6 5.6 6.1.7-4.5 4.2 1.2 6-5.4-3-5.4 3 1.2-6-4.5-4.2 6.1-.7L12 3.5Z',
  arrowRight: 'M5 12h14M13 6l6 6-6 6',
  mail: 'M4 6h16v12H4z M4 7l8 6 8-6',
  phone:
    'M6.6 10.8c1.4 2.8 3.8 5.2 6.6 6.6l2.2-2.2c.3-.3.7-.4 1.1-.3 1.2.4 2.5.6 3.8.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1C11.4 21 3 12.6 3 3c0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.6.6 3.8.1.4 0 .8-.3 1.1l-2.2 2.9Z',
  mapPin:
    'M12 21s7-6.3 7-11.5A7 7 0 0 0 5 9.5C5 14.7 12 21 12 21Z M12 12a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z',
  facebook:
    'M14 8h2.5V4.5H14C11.5 4.5 10 6.2 10 8.7V11H7.5v3.5H10V21h3.5v-6.5H16l.5-3.5h-3V9c0-.6.2-1 1.5-1Z',
  instagram:
    'M4 8a4 4 0 0 1 4-4h8a4 4 0 0 1 4 4v8a4 4 0 0 1-4 4H8a4 4 0 0 1-4-4V8Z M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z M16.5 7.5h.01',
  x: 'M4 4l16 16M20 4L4 20',
  quote:
    'M7 8.5C5 9.7 4 11.4 4 13.5S5 17 7 17s3.2-1.2 3.2-3S9.5 11 8 11c.2-1.3 1.2-2.6 3-3.5L9.5 6C8.5 6.5 7.6 7.3 7 8.5Z M15.8 8.5c-2 1.2-3 2.9-3 5s1 3.5 3 3.5 3.2-1.2 3.2-3-.7-3-2.2-3c.2-1.3 1.2-2.6 3-3.5L18.3 6c-1 .5-1.9 1.3-2.5 2.5Z',
} as const;

export type IconName = keyof typeof paths;

interface IconProps extends Omit<SVGAttributes<SVGSVGElement>, 'children'> {
  readonly name: IconName;
  readonly size?: number;
  /** Fill instead of stroke — used only for the star rating glyph. */
  readonly filled?: boolean;
}

export function Icon({ name, size = 20, filled = false, ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={filled ? 'currentColor' : 'none'}
      stroke={filled ? 'none' : 'currentColor'}
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      <path d={paths[name]} />
    </svg>
  );
}
